import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Projet } from 'app/models/projet';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProjetsService {
  base_path = 'https://app.titv-store-api.com/';
  agencyIdSource: any;
  
  constructor(private http: HttpClient) { }
  
  private refreshData = new Subject<void>();
  
  get refreshNedeed() {
    return this.refreshData;
  }

  // ✅ Get token from session storage
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  // ✅ Build headers with authorization token
  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      });
    }
    
    return new HttpHeaders({
      'Content-Type': 'application/json'
    });
  }

  // ✅ Handle HTTP errors – ARROW FUNCTION to preserve `this`
  private handleError = (error: HttpErrorResponse) => {
    let errorMessage = 'Une erreur est survenue';
    
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    
    console.error('Erreur ProjetsService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // Create a new item
  createProjet(item: any): Observable<Projet> {
    const headers = this.getHeaders();
    
    return this.http.post<Projet>(
      this.base_path + 'projets/add', 
      JSON.stringify(item), 
      { headers }
    ).pipe(
      tap(() => {
        this.refreshData.next();
      }),
      catchError(this.handleError)
    );
  }

  // Get single Projet data by ID
  getProjet(id: any): Observable<Projet> {
    const headers = this.getHeaders();
    
    return this.http.get<Projet>(
      this.base_path + 'projets/' + id,
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  // Get Projets data
  getListProjet(): Observable<Projet[]> {
    const headers = this.getHeaders();
    
    return this.http.get<Projet[]>(
      this.base_path + 'projets/all',
      { headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  // Update item by id
  updateProjet(id: any, item: any): Observable<Projet> {
    const headers = this.getHeaders();
    
    return this.http.put<Projet>(
      this.base_path + 'projets/edit/' + id, 
      JSON.stringify(item), 
      { headers }
    ).pipe(
      tap(() => {
        this.refreshData.next();
      }),
      catchError(this.handleError)
    );
  }

  // ✅ NEW: Update only the display order
  updateProjetOrder(id: string, displayOrder: number): Observable<any> {
    const headers = this.getHeaders();
    const body = { displayOrder: displayOrder };
    
    return this.http.put(
      this.base_path + 'projets/update-order/' + id,
      body,
      { headers, responseType: 'text' }
    ).pipe(
      tap(() => {
        this.refreshData.next();
      }),
      catchError(this.handleError)
    );
  }

  // Delete item by id
  deleteProjet(id: any): Observable<any> {
    const headers = this.getHeaders();
    
    return this.http.delete(
      this.base_path + 'projets/delete/' + id,
      { 
        headers,
        responseType: 'text' 
      }
    ).pipe(
      tap(() => {
        this.refreshData.next();
      }),
      catchError(this.handleError)
    );
  }
}